import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { Button } from '@/components/ui';
import { Icon } from '@/components/Icons';
import { flushSyncQueue } from '@/data/hybrid-sync';

declare const __APP_BUILD_ID__: string | undefined;

type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

let updateServiceWorker: UpdateServiceWorker | null = null;
let registrationStarted = false;

const CURRENT_BUILD_ID = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';
const DISMISSED_BUILD_KEY = 'taskflow.dismissed_build_id';
const CLOUD_ORIGIN = 'https://taskflow-7qp.pages.dev';

/**
 * شريط الإشعار بالتحديث الفوري التلقائي في الخلفية لجميع الأجهزة
 * (الويب، تطبيق الويندوز، وتطبيق الجوال APK).
 *
 * يفحص وجود أي تحديث جديد دورياً وعند عودة الاتصال أو فتح النافذة،
 * ويعرض إشعاراً واضحاً للمستخدم وبمجرد الموافقة يتحدث التطبيق في الخلفية فوراً.
 */
export function PwaUpdateBanner() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  // Auto-dismiss offlineReady banner after 4s so it never clutters mobile screen
  useEffect(() => {
    if (offlineReady && !needRefresh) {
      const timer = setTimeout(() => {
        setOfflineReady(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [offlineReady, needRefresh]);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const remoteBuildIdRef = useRef<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (import.meta.env.DEV) {
      return () => {
        mounted.current = false;
      };
    }

    let swRegistration: ServiceWorkerRegistration | undefined;

    async function checkRemoteVersion() {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      try {
        if (swRegistration) {
          void swRegistration.update().catch(() => null);
        }

        const isNativeOrLocalHost =
          typeof window !== 'undefined' &&
          (Boolean(window.taskflowAndroid) ||
            window.location.hostname === 'localhost' ||
            window.location.protocol === 'capacitor:');

        const versionUrl = isNativeOrLocalHost
          ? `${CLOUD_ORIGIN}/version.json?t=${Date.now()}`
          : `/version.json?t=${Date.now()}`;

        const response = await fetch(versionUrl, {
          cache: 'no-store',
        });
        if (!response.ok) return;
        const data = (await response.json()) as { buildId?: string };
        if (
          data?.buildId &&
          CURRENT_BUILD_ID !== 'dev' &&
          data.buildId !== CURRENT_BUILD_ID
        ) {
          remoteBuildIdRef.current = data.buildId;
          const dismissedId = sessionStorage.getItem(DISMISSED_BUILD_KEY);
          if (mounted.current && dismissedId !== data.buildId) {
            setNeedRefresh(true);
            setDismissed(false);
          }
        }
      } catch {
        // تجاهل أخطاء فحص النسخة عند ضعف الشبكة
      }
    }

    if (
      !registrationStarted &&
      'serviceWorker' in navigator &&
      !(typeof window !== 'undefined' && window.taskflowDesktop?.isDesktop)
    ) {
      registrationStarted = true;
      try {
        updateServiceWorker = registerSW({
          immediate: true,
          onRegisteredSW(_swUrl, registration) {
            swRegistration = registration;
            if (registration) {
              void registration.update().catch(() => null);
            }
          },
          onNeedRefresh() {
            if (mounted.current) {
              setNeedRefresh(true);
              setDismissed(false);
            }
          },
          onOfflineReady() {
            if (mounted.current) setOfflineReady(true);
          },
          onRegisterError(error) {
            console.warn('Service worker registration failed', error instanceof Error ? error.message : error);
          },
        });
      } catch {
        // بيئة لا تدعم عامل الخدمة
      }
    }

    // فحص فوري ودوري كل 25 ثانية وعند العودة للتطبيق أو استعادة الإنترنت
    const initialTimer = setTimeout(() => void checkRemoteVersion(), 3000);
    const interval = setInterval(() => void checkRemoteVersion(), 25_000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void checkRemoteVersion();
      }
    };
    const onOnline = () => void checkRemoteVersion();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onOnline);

    return () => {
      mounted.current = false;
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onOnline);
    };
  }, []);

  async function applyBackgroundUpdate() {
    setUpdating(true);
    try {
      // مزامنة أي عمليات محلية معلقة أولاً لضمان عدم ضياع أي عمل
      await flushSyncQueue().catch(() => null);

      // 1. تحديث تطبيق الويندوز (Electron) في الخلفية
      if (typeof window !== 'undefined' && window.taskflowDesktop?.applyOtaUpdate) {
        await window.taskflowDesktop.applyOtaUpdate().catch(() => null);
        window.location.reload();
        return;
      }

      // 2. تحديث تطبيق الأندرويد (APK) في الخلفية
      if (typeof window !== 'undefined' && window.taskflowAndroid?.applyOtaUpdate) {
        window.taskflowAndroid.applyOtaUpdate();
        return;
      }

      // 3. تحديث تطبيق الويب وPWA على المتصفح والآيفون والآيباد والأندرويد
      if (updateServiceWorker) {
        await updateServiceWorker(true).catch(() => null);
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(async (reg) => {
            await reg.update().catch(() => null);
            if (reg.waiting) {
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          }),
        );
      }

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(
          keys
            .filter((k) => !k.includes('google-fonts'))
            .map((k) => caches.delete(k)),
        );
      }
    } catch {
      // تجاهل
    } finally {
      window.location.reload();
    }
  }

  if (dismissed || (!needRefresh && !offlineReady)) return null;

  return (
    <div className="pwa-banner" role="status" aria-live="polite">
      <span className="pwa-banner-icon">
        <Icon name={needRefresh ? 'refresh' : 'check-circle'} size={18} className={updating ? 'spin' : undefined} />
      </span>
      <div className="grow">
        <div className="strong small">
          {needRefresh
            ? 'يتوفر تحديث جديد للتطبيق الآن'
            : 'التطبيق جاهز للعمل بإنترنت وبدون إنترنت'}
        </div>
        <div className="tiny muted">
          {needRefresh
            ? 'تمت إضافة تحسينات جديدة. اضغط للموافقة والتحديث الفوري في الخلفية مع حفظ كامل بياناتك.'
            : 'تُحفظ بياناتك محلياً وسحابياً معاً وتُزامن تلقائياً فور توفر الاتصال.'}
        </div>
      </div>
      {needRefresh ? (
        <Button
          size="sm"
          variant="primary"
          disabled={updating}
          onClick={() => void applyBackgroundUpdate()}
        >
          {updating ? 'جاري التحديث...' : 'تحديث الآن'}
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        disabled={updating}
        onClick={() => {
          if (remoteBuildIdRef.current) {
            sessionStorage.setItem(DISMISSED_BUILD_KEY, remoteBuildIdRef.current);
          }
          setDismissed(true);
          setOfflineReady(false);
        }}
      >
        لاحقًا
      </Button>
    </div>
  );
}
